import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, getCurrentTier } from '@gad/shared';
import { diagnosisApi } from '../lib/api';

interface Criminal {
    id: number;
    asset_id: string;
    ad_id?: string;
    asset_text: string;
    field_type: 'HEADLINE' | 'DESCRIPTION';
    campaign_id: string;
    campaign_name: string;
    ad_group_id: string;
    ad_group_name: string;
    criminal_type: 'IMMEDIATE_REPLACE' | 'QUALITY_REPLACE';
    reason: string;
    total_impressions: number;
    total_clicks: number;
    total_conversions: number;
    total_cost_micros: number;
    cpl: number | null;
    last_conversion_date: string;
    days_since_last_conversion: number;
    tier_level: string;
    ai_suggested_text?: string;
    human_decision: string;
    recent_14_day_cost_micros?: number;
}

interface CriminalGroup {
    key: string;
    campaign_id: string;
    campaign_name: string;
    ad_group_id: string;
    ad_group_name: string;
    items: Criminal[];
    item_count: number;
    total_cost_micros: number;
    total_recent_14_day_cost_micros: number;
    total_impressions: number;
    total_clicks: number;
    total_conversions: number;
}

interface Summary {
    tier: { tier: string; name: string; cplThreshold: number };
    immediateReplace: number;
    qualityReplace: number;
    totalPendingCost: number;
}

// Google Ads URL 生成器
const CUSTOMER_ID = '1709686978'; // 子帳戶 ID
function getGoogleAdsUrl(type: 'campaign' | 'adgroup', id: string) {
    if (type === 'campaign') {
        return `https://ads.google.com/aw/campaigns?campaignId=${id}&__u=${CUSTOMER_ID}`;
    } else {
        return `https://ads.google.com/aw/adgroups?adGroupId=${id}&__u=${CUSTOMER_ID}`;
    }
}

function getCtr(criminal: Criminal): number {
    if (!criminal.total_impressions || criminal.total_impressions <= 0) return 0;
    return criminal.total_clicks / criminal.total_impressions;
}

export function Dashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [criminals, setCriminals] = useState<Criminal[]>([]);
    const [error, setError] = useState<string | null>(null);

    const currentTier = getCurrentTier(new Date().getMonth() + 1);
    const groupedCriminals = useMemo<CriminalGroup[]>(() => {
        const map = new Map<string, CriminalGroup>();

        for (const criminal of criminals) {
            const key = `${criminal.campaign_id}::${criminal.ad_group_id}`;
            const existing = map.get(key);
            if (existing) {
                existing.items.push(criminal);
                existing.item_count += 1;
                existing.total_cost_micros += criminal.total_cost_micros || 0;
                existing.total_recent_14_day_cost_micros += criminal.recent_14_day_cost_micros || 0;
                existing.total_impressions += criminal.total_impressions || 0;
                existing.total_clicks += criminal.total_clicks || 0;
                existing.total_conversions += criminal.total_conversions || 0;
            } else {
                map.set(key, {
                    key,
                    campaign_id: criminal.campaign_id,
                    campaign_name: criminal.campaign_name,
                    ad_group_id: criminal.ad_group_id,
                    ad_group_name: criminal.ad_group_name,
                    items: [criminal],
                    item_count: 1,
                    total_cost_micros: criminal.total_cost_micros || 0,
                    total_recent_14_day_cost_micros: criminal.recent_14_day_cost_micros || 0,
                    total_impressions: criminal.total_impressions || 0,
                    total_clicks: criminal.total_clicks || 0,
                    total_conversions: criminal.total_conversions || 0
                });
            }
        }

        return Array.from(map.values())
            .map(group => ({
                ...group,
                items: group.items.sort((a, b) => {
                    const aCtr = getCtr(a);
                    const bCtr = getCtr(b);
                    if (aCtr !== bCtr) return aCtr - bCtr;
                    return (a.total_clicks || 0) - (b.total_clicks || 0);
                })
            }))
            .sort((a, b) => b.total_recent_14_day_cost_micros - a.total_recent_14_day_cost_micros);
    }, [criminals]);

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        try {
            setLoading(true);
            setError(null);

            // 載入摘要
            const summaryResult = await diagnosisApi.getSummary();
            if (summaryResult.success && summaryResult.data) {
                setSummary(summaryResult.data as Summary);
            }

            // 載入戰犯清單
            const criminalsResult = await diagnosisApi.getCriminals('PENDING');
            if (criminalsResult.success && criminalsResult.data) {
                setCriminals(criminalsResult.data as Criminal[]);
            }

        } catch (err) {
            setError('無法載入數據');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    async function runDiagnosis() {
        try {
            setLoading(true);
            setError(null);
            const result = await diagnosisApi.runDiagnosis();
            if (result.success) {
                await loadData();
            } else {
                setError(result.error || '診斷失敗');
            }
        } catch (err) {
            setError('診斷失敗');
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h2 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>
                        🎯 週一優化提案
                    </h2>
                    <p style={{ color: 'var(--color-text-secondary)' }}>
                        {new Date().toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric' })} 更新
                    </p>
                </div>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <span className={`tier-badge tier-${currentTier.tier.toLowerCase()}`}>
                        Tier {currentTier.tier} · {currentTier.name}
                    </span>
                    <button className="btn btn-primary" onClick={runDiagnosis}>
                        🔄 重新診斷
                    </button>
                </div>
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                }}
            >
                <div
                    className="card"
                    style={{
                        border: '1px solid rgba(14, 165, 233, 0.24)',
                        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(34, 197, 94, 0.06) 100%)'
                    }}
                >
                    <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0284c7', letterSpacing: '0.08em' }}>
                            SEARCH / RSA
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800 }}>
                            搜尋廣告建立器
                        </h3>
                        <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                            直接用關鍵字、Persona、場景與特式化生成 RSA 標題與說明，適合一般搜尋廣告文案製作。
                        </p>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/ad-creator?mode=search')}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        前往 Search 建立器
                    </button>
                </div>

                <div
                    className="card"
                    style={{
                        border: '1px solid rgba(99, 102, 241, 0.24)',
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.06) 100%)'
                    }}
                >
                    <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '1rem' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#4f46e5', letterSpacing: '0.08em' }}>
                            PMAX
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800 }}>
                            PMAX 規劃工具
                        </h3>
                        <p style={{ margin: 0, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                            依進場入口、場景、Landing Page、Search Themes 與 Audience Signals 規劃 asset group 對應文案。
                        </p>
                    </div>
                    <button
                        className="btn"
                        onClick={() => navigate('/ad-creator?mode=pmax')}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        前往 PMAX 規劃
                    </button>
                </div>
            </div>

            {error && (
                <div className="card" style={{ borderColor: 'var(--color-danger)', marginBottom: '1rem' }}>
                    ❌ {error}
                </div>
            )}

            {/* 統計卡片 */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-label">需立即替換</div>
                    <div className="stat-value danger">
                        {summary?.immediateReplace ?? 0}
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">品質警告</div>
                    <div className="stat-value warning">
                        {summary?.qualityReplace ?? 0}
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">待處理花費</div>
                    <div className="stat-value">
                        {formatCurrency(summary?.totalPendingCost ?? 0)}
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">CPL 警戒線</div>
                    <div className="stat-value">
                        {formatCurrency(currentTier.cplThreshold)}
                    </div>
                </div>
            </div>

            {/* 戰犯清單 */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">🚨 戰犯資產清單</h3>
                    <span style={{ color: 'var(--color-text-secondary)' }}>共 {criminals.length} 筆</span>
                </div>

                {criminals.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
                        ✅ 目前沒有需要處理的戰犯資產
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                        {groupedCriminals.map((group) => (
                            <div key={group.key} className="card" style={{ border: '1px solid var(--color-border)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                    <div className="criminal-breadcrumb" style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--color-text)' }}>
                                        <a
                                            href={getGoogleAdsUrl('campaign', group.campaign_id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 700 }}
                                        >
                                            📁 {group.campaign_name || '未知活動'}
                                        </a>
                                        <span style={{ margin: '0 0.5rem' }}>›</span>
                                        <a
                                            href={getGoogleAdsUrl('adgroup', group.ad_group_id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: 'var(--color-primary)', textDecoration: 'none', fontWeight: 700 }}
                                        >
                                            📂 {group.ad_group_name || '未知群組'}
                                        </a>
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>待替換 {group.item_count} 筆</div>
                                </div>

                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>
                                    <span>📅 14天花費: {formatCurrency(group.total_recent_14_day_cost_micros / 1_000_000)}</span>
                                    <span>💰 累計花費: {formatCurrency(group.total_cost_micros / 1_000_000)}</span>
                                    <span>👁️ 曝光: {group.total_impressions.toLocaleString()}</span>
                                    <span>🖱️ 點擊: {group.total_clicks.toLocaleString()}</span>
                                    <span>📝 轉換: {group.total_conversions}</span>
                                </div>

                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                <th style={{ textAlign: 'left', padding: '0.6rem', fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>文案內容</th>
                                                <th style={{ textAlign: 'right', padding: '0.6rem', width: '80px', fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>點擊</th>
                                                <th style={{ textAlign: 'right', padding: '0.6rem', width: '90px', fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>CTR</th>
                                                <th style={{ textAlign: 'right', padding: '0.6rem', width: '92px', fontSize: '0.78rem', fontWeight: 600, color: 'var(--color-text-secondary)' }}>操作</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.items.map((criminal) => (
                                                <tr key={criminal.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                    <td style={{ padding: '0.6rem', fontSize: '0.86rem', fontWeight: 400, lineHeight: 1.35, color: 'var(--color-text-secondary)' }}>
                                                        {criminal.asset_text}
                                                    </td>
                                                    <td style={{ padding: '0.6rem', textAlign: 'right', fontSize: '0.84rem', color: 'var(--color-text-secondary)' }}>
                                                        {criminal.total_clicks || 0}
                                                    </td>
                                                    <td style={{ padding: '0.6rem', textAlign: 'right', fontSize: '0.84rem', color: 'var(--color-text-secondary)' }}>
                                                        {(getCtr(criminal) * 100).toFixed(2)}%
                                                    </td>
                                                    <td style={{ padding: '0.6rem', textAlign: 'right' }}>
                                                        <button
                                                            className="btn btn-outline"
                                                            style={{ fontSize: '0.74rem', padding: '0.22rem 0.45rem' }}
                                                            onClick={() => navigate(`/editor/${criminal.id}`)}
                                                        >
                                                            編輯
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
